import { showMessage } from './toast.js';
import { getAppConfig } from './config';

// Helper: get CSRF token (prefer drop area's data attribute, fallback to cookie)
function getCSRFToken() {
  const dropArea = document.getElementById('drop-upload');
  if (dropArea && dropArea.dataset.csrf) {
    return dropArea.dataset.csrf;
  }
  const match = document.cookie.match(/(?:^|; )csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

// Shared uploader used by both drag-and-drop and clipboard flows
function textToCsv(text) {
  // Normalize and convert to CSV if tab-delimited
  const normalized = text.replace(/\r\n/g, '\n').trim();
  if (/\t/.test(normalized)) {
    return normalized
      .split('\n')
      .map((line) =>
        line
          .split('\t')
          .map((v) => '"' + String(v).replace(/"/g, '""') + '"')
          .join(',')
      )
      .join('\n');
  }
  return normalized;
}

function dateStampYYYYMMDD(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function clipboardFilenameWithDate(prefix = 'Clipboard', date = new Date()) {
  return `${prefix}-${dateStampYYYYMMDD(date)}.csv`;
}

function fileFromClipboardText(text) {
  const csvText = textToCsv(text);
  const name = clipboardFilenameWithDate();
  return new File([csvText], name, { type: 'text/csv' });
}


  const config = getAppConfig()

function uploadPriceImport(file, csrf) {

  const formData = new FormData();
  formData.append('file', file);

  const tbody = document.getElementById('unfinished-price-lists');
  const fileElement = document.createElement('tr');
  fileElement.insertAdjacentHTML(
    'afterbegin',
    '<td>' +
      file.name +
      '</td><td><div class="spinner-border" role="status">\n' +
      '  <span class="visually-hidden">Loading...</span>\n' +
      '</div></td>',
  );

  if (tbody) {
    tbody.prepend(fileElement);
  }

  const emptyRow = document.getElementById('price-import-empty');
  if (emptyRow) {
    emptyRow.remove();
  }

  fetch(config.priceListUploadUrl, {
    method: 'POST',
    body: formData,
    headers: {
      'X-CSRFToken': csrf,
      Accept: 'application/json',
    },
  })
    .then((r) => {
      if (!r.ok) {
        if (r.status === 500) {
          showMessage('error', 'An error occurred while uploading the file');

          const element = fileElement.querySelector('td:last-child');
          fileElement.querySelectorAll('td').forEach((e) => {
            e.classList.remove('bg-danger');
          });
          if (element) element.textContent = 'Errored!';
        }
        r
          .json()
          .then(function (data) {
            if (data && data.errors && data.errors['file']) {
              data.errors['file'].forEach((message) => {
                showMessage('error', message);
                const element = fileElement.querySelector('td:last-child');
                fileElement.querySelectorAll('td').forEach((e) => {
                  e.classList.remove('bg-danger');
                });
                if (element) element.innerHTML = message;
              });
            }
          })
          .catch(() => {
            // ignore json parse error
          });
      } else {
        showMessage('success', 'File uploaded successfully');
        r.text().then((body) => {
          if (tbody) tbody.insertAdjacentHTML('afterbegin', body);
          fileElement.remove();
        });
      }
    })
    .catch(() => {
      showMessage('error', 'An error occurred while uploading the file');
      const element = fileElement.querySelector('td:last-child');
      fileElement.querySelectorAll('td').forEach((e) => {
        e.classList.remove('bg-danger');
      });
      if (element) element.textContent = 'Errored!';
    });
}

function initDragAndDropUpload() {
  const dropArea = document.getElementById('drop-upload');

  if (dropArea != null) {
    const csrf = dropArea.dataset.csrf;

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
      dropArea.addEventListener(eventName, preventDefaults, false);
    });

    function preventDefaults(e) {
      e.preventDefault();
      e.stopPropagation();
    }

    ['dragenter', 'dragover'].forEach((eventName) => {
      dropArea.addEventListener(eventName, highlight, false);
    });
    ['dragleave', 'drop'].forEach((eventName) => {
      dropArea.addEventListener(eventName, unhighlight, false);
    });

    function handleDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      let dt = e.dataTransfer;
      let files = dt.files;

      handleFiles(files);
    }

    function handleFiles(files) {
      [...files].forEach((file) => uploadPriceImport(file, csrf));
    }

    function highlight(e) {
      dropArea.classList.remove('bg-opacity-25');
      dropArea.classList.add('bg-opacity-50');
    }

    function unhighlight(e) {
      dropArea.classList.remove('bg-opacity-50');
      dropArea.classList.add('bg-opacity-25');
    }

    dropArea.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      handleDrop(e);
    });
  }
}

function initClipboardUpload() {
  const btn = document.getElementById('clipboard-upload');
  if (!btn) return;

  btn.addEventListener('click', async (e) => {
    e.preventDefault();

    if (!navigator.clipboard || !navigator.clipboard.readText) {
      showMessage(
        'error',
        "Your browser doesn't allow reading from the clipboard. Please paste into a file and upload.",
      );
      return;
    }

    try {
      let text = await navigator.clipboard.readText();
      if (!text || text.trim() === '') {
        showMessage('warning', 'Clipboard is empty or contains no text');
        return;
      }

      // Normalize and convert to CSV if tab-delimited
      let normalized = text.replace(/\r\n/g, '\n').trim();
      let csvText = normalized;
      if (/\t/.test(normalized)) {
        csvText = normalized
          .split('\n')
          .map((line) =>
            line
              .split('\t')
              .map((v) => '"' + String(v).replace(/"/g, '""') + '"')
              .join(','),
          )
          .join('\n');
      }

      const file = new File([csvText], clipboardFilenameWithDate(), { type: 'text/csv' });
      const csrf = getCSRFToken();
      if (!csrf) {
        showMessage('error', 'Missing CSRF token. Please refresh the page and try again.');
        return;
      }
      uploadPriceImport(file, csrf);
    } catch (err) {
      console.error(err);
      showMessage(
        'error',
        'Could not read from clipboard. Please allow clipboard access or paste into a file and upload.',
      );
    }
  });
}


function downloadQuickOffer(){
  const params = new URLSearchParams(window.location.search);
  const download = params.get("download");

  if (download) {
    // Trigger the Excel download
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = `/suppliers/quick-offers/${download}/download`;   // your real export endpoint
    document.body.appendChild(iframe);

    showMessage(
        'success',
        "Your offer is being downloaded",
      );
    // Optional: clean URL so it doesn't re-download on refresh
    const url = new URL(window.location.href);
    url.searchParams.delete("download");
    history.replaceState(null, "", url.toString());
  }
};

window.addEventListener('DOMContentLoaded', () => {
  window.addEventListener('htmx:load', () => {
    initDragAndDropUpload();
    initClipboardUpload();
    initPasteUpload();
    downloadQuickOffer();
  });
});


// Enable Ctrl/Cmd+V paste-to-upload on the dashboard
function initPasteUpload() {
  if (window.__manifestoPasteUploadBound) return;
  window.__manifestoPasteUploadBound = true;

  document.addEventListener('paste', async (e) => {
    // Only on dashboard-like pages where upload is available
    const onDashboard = !!document.getElementById('drop-upload') || !!document.getElementById('clipboard-upload');
    if (!onDashboard) return;

    // Do not hijack pasting inside editable fields
    const ae = document.activeElement;
    if (ae) {
      const tag = ae.tagName ? ae.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || ae.isContentEditable) {
        return;
      }
    }

    // Prefer event clipboard text
    let text = '';
    if (e.clipboardData && typeof e.clipboardData.getData === 'function') {
      text = e.clipboardData.getData('text/plain') || e.clipboardData.getData('text') || '';
      // If files are present and no text, ignore (do not block other handlers)
      if (!text && e.clipboardData.files && e.clipboardData.files.length > 0) {
        return;
      }
    }

    // Fallback to async clipboard API if allowed
    if (!text) {
      try {
        if (navigator.clipboard && navigator.clipboard.readText) {
          text = await navigator.clipboard.readText();
        }
      } catch (err) {
        // ignore
      }
    }

    if (!text || text.trim() === '') {
      return; // nothing to upload
    }

    // Prevent stray text insertion into the page when we handle the upload
    e.preventDefault();

    // Normalize and convert to CSV if tab-delimited
    let normalized = text.replace(/\r\n/g, '\n').trim();
    let csvText = normalized;
    if (/\t/.test(normalized)) {
      csvText = normalized
        .split('\n')
        .map((line) =>
          line
            .split('\t')
            .map((v) => '"' + String(v).replace(/"/g, '""') + '"')
            .join(',')
        )
        .join('\n');
    }

    const file = new File([csvText], clipboardFilenameWithDate(), { type: 'text/csv' });
    const csrf = getCSRFToken();
    if (!csrf) {
      showMessage('error', 'Missing CSRF token. Please refresh the page and try again.');
      return;
    }
    uploadPriceImport(file, csrf);
  });
}
